class AccountDrawer extends HTMLElement {
  constructor() {
    super()

    this.inner = this.querySelector('[data-account-drawer-inner]')
    this.body = this.querySelector('.account-drawer__body')
    this.section = this.closest('.shopify-section')
    this.isOpen = false
    this.lastFocused = null

    this.onToggleClick = this.onToggleClick.bind(this)
    this.onLinkClick = this.onLinkClick.bind(this)
    this.onHashChange = this.onHashChange.bind(this)
    this.onKeyup = this.onKeyup.bind(this)
    this.close = this.close.bind(this)
    this.onEditorSelect = this.onEditorSelect.bind(this)
    this.onEditorDeselect = this.onEditorDeselect.bind(this)
    this.onDragMove = this.onDragMove.bind(this)
    this.onCartChange = this.onCartChange.bind(this)
    this.onDragEnd = this.onDragEnd.bind(this)
  }

  connectedCallback() {
    document.addEventListener('click', this.onToggleClick)
    document.addEventListener('click', this.onLinkClick)
    window.addEventListener('hashchange', this.onHashChange)
    document.addEventListener('theme:cart-drawer:open', this.close)
    document.addEventListener('theme:cart:change', this.onCartChange)
    document.addEventListener('shopify:section:select', this.onEditorSelect)
    document.addEventListener('shopify:block:select', this.onEditorSelect)
    document.addEventListener('shopify:section:deselect', this.onEditorDeselect)
    this.addEventListener('keyup', this.onKeyup)
    this.addEventListener('click', (event) => this.onClick(event))
    this.addEventListener('submit', (event) => this.onSubmit(event))
    this.addEventListener('change', (event) => this.onChange(event))
    this.addEventListener('pointerdown', (event) => this.onDragStart(event))
    this.addEventListener('input', (event) => this.onDateInput(event))
    this.addEventListener('dragstart', (event) => {
      if (event.target.closest('.account-drawer__carousel')) event.preventDefault()
    })

    this.initCountries()
    this.reopenAfterLogin()
    this.promptProfileCompletion()
    this.openFromUrl()
  }

  // Deep links: #login / #sign-up (on-site) and ?account_modal=login|sign-up (off-site, e.g. emails)
  static LINK_TABS = { login: 'login', 'sign-up': 'register' }

  tabFromLink(value) {
    return AccountDrawer.LINK_TABS[String(value || '').replace(/^#/, '').toLowerCase()] || null
  }

  openFromUrl() {
    const url = new URL(window.location.href)
    const tab = this.tabFromLink(url.searchParams.get('account_modal')) || this.tabFromLink(url.hash)
    if (!tab) return

    // Strip the trigger so a refresh or share doesn't re-open the drawer
    url.searchParams.delete('account_modal')
    if (this.tabFromLink(url.hash)) url.hash = ''
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)

    const openDrawer = () => this.openTab(tab)

    if (document.readyState === 'complete') {
      openDrawer()
    } else {
      window.addEventListener('load', openDrawer, { once: true })
    }
  }

  onHashChange() {
    if (this.tabFromLink(window.location.hash)) this.openFromUrl()
  }

  openTab(tab) {
    // Signed-in customers have no login/register panels; just show the drawer
    if (this.querySelector(`[data-account-drawer-panel="${tab}"]`)) this.activateTab(tab)
    requestAnimationFrame(() => this.open())
  }

  onLinkClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest('a[href*="#"]')
    if (!link) return

    const url = new URL(link.getAttribute('href'), window.location.href)
    const tab = this.tabFromLink(url.hash)
    if (!tab || url.origin !== window.location.origin || url.pathname !== window.location.pathname) return

    event.preventDefault()
    this.lastFocused = link
    this.openTab(tab)
  }

  promptProfileCompletion() {
    if (!this.querySelector('[data-account-drawer-complete]') || window.Shopify?.designMode) return

    try {
      const key = `accountDrawerCompletePrompted:${this.dataset.customerId}`
      if (window.sessionStorage.getItem(key)) return
      window.sessionStorage.setItem(key, '1')
    } catch (error) {}

    const openDrawer = () => requestAnimationFrame(() => this.open())

    if (document.readyState === 'complete') {
      openDrawer()
    } else {
      window.addEventListener('load', openDrawer, { once: true })
    }
  }

  rememberLogin() {
    try {
      window.localStorage.setItem('accountDrawerLogin', String(Date.now()))
    } catch (error) {}
  }

  reopenAfterLogin() {
    let startedAt = 0

    try {
      startedAt = Number(window.localStorage.getItem('accountDrawerLogin')) || 0
      if (startedAt && this.dataset.customerId) window.localStorage.removeItem('accountDrawerLogin')
    } catch (error) {
      return
    }

    const isRecent = startedAt && Date.now() - startedAt < 10 * 60 * 1000
    if (!isRecent || !this.dataset.customerId) return

    const openDrawer = () => {
      this.activateTab('foryou')
      requestAnimationFrame(() => this.open())
    }

    if (document.readyState === 'complete') {
      openDrawer()
    } else {
      window.addEventListener('load', openDrawer, { once: true })
    }
  }

  initCountries() {
    this.querySelectorAll('[data-account-drawer-country]').forEach((select) => this.initCountry(select))
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onToggleClick)
    document.removeEventListener('click', this.onLinkClick)
    window.removeEventListener('hashchange', this.onHashChange)
    document.removeEventListener('theme:cart-drawer:open', this.close)
    document.removeEventListener('theme:cart:change', this.onCartChange)
    document.removeEventListener('shopify:section:select', this.onEditorSelect)
    document.removeEventListener('shopify:block:select', this.onEditorSelect)
    document.removeEventListener('shopify:section:deselect', this.onEditorDeselect)
  }

  onDragStart(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return

    const carousel = event.target.closest('.account-drawer__carousel')
    if (!carousel || event.target.closest('button, input, select')) return
    if (carousel.scrollWidth <= carousel.clientWidth) return

    this.drag = {
      carousel,
      startX: event.clientX,
      startScroll: carousel.scrollLeft,
      moved: false,
    }

    document.addEventListener('pointermove', this.onDragMove)
    document.addEventListener('pointerup', this.onDragEnd)
    document.addEventListener('pointercancel', this.onDragEnd)
  }

  onDragMove(event) {
    if (!this.drag) return

    const distance = event.clientX - this.drag.startX

    if (!this.drag.moved && Math.abs(distance) > 5) {
      this.drag.moved = true
      this.drag.carousel.classList.add('is-dragging')
    }

    if (this.drag.moved) {
      event.preventDefault()
      this.drag.carousel.scrollLeft = this.drag.startScroll - distance
    }
  }

  onDragEnd() {
    if (!this.drag) return

    const { carousel, moved } = this.drag
    this.drag = null

    document.removeEventListener('pointermove', this.onDragMove)
    document.removeEventListener('pointerup', this.onDragEnd)
    document.removeEventListener('pointercancel', this.onDragEnd)

    if (!moved) return

    carousel.addEventListener(
      'click',
      (event) => {
        event.preventDefault()
        event.stopPropagation()
      },
      { capture: true, once: true }
    )

    const snapPoint = Array.from(carousel.children).reduce(
      (closest, item) => {
        const offset = item.offsetLeft - carousel.firstElementChild.offsetLeft
        return Math.abs(offset - carousel.scrollLeft) < Math.abs(closest - carousel.scrollLeft) ? offset : closest
      },
      0
    )

    carousel.scrollTo({ left: snapPoint, behavior: 'smooth' })
    setTimeout(() => carousel.classList.remove('is-dragging'), 350)
  }

  onToggleClick(event) {
    const toggle = event.target.closest('[data-account-drawer-toggle]')
    if (!toggle) return

    event.preventDefault()
    this.lastFocused = toggle
    this.open()
  }

  onEditorSelect(event) {
    if (this.section && (event.target === this.section || this.section.contains(event.target))) {
      this.open()
    }
  }

  onEditorDeselect(event) {
    if (this.section && event.target === this.section) this.close()
  }

  onKeyup(event) {
    if (event.code === 'Escape') this.close()
  }

  open() {
    if (this.isOpen) return

    this.isOpen = true
    this.classList.remove('is-closing')
    this.classList.add('is-open')
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }))

    this.inner.addEventListener(
      'animationend',
      () => {
        const focusTarget = this.querySelector('[data-account-drawer-close]')
        if (focusTarget) focusTarget.focus()
      },
      { once: true }
    )
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    this.classList.add('is-closing')
    this.classList.remove('is-open')
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }))

    this.inner.addEventListener('animationend', () => this.classList.remove('is-closing'), { once: true })

    if (this.lastFocused) {
      this.lastFocused.focus()
      this.lastFocused = null
    }
  }

  onClick(event) {
    const target = event.target

    if (target.closest('.account-drawer__signin')) {
      this.rememberLogin()
      return
    }

    if (target.hasAttribute('data-drawer-underlay') || target.closest('[data-account-drawer-close]')) {
      event.preventDefault()
      this.close()
      return
    }

    const tab = target.closest('[data-account-drawer-tab]')
    if (tab) {
      this.activateTab(tab.dataset.accountDrawerTab)
      return
    }

    const tabTarget = target.closest('[data-account-drawer-tab-target]')
    if (tabTarget) {
      this.activateTab(tabTarget.dataset.accountDrawerTabTarget)
      return
    }

    const viewTarget = target.closest('[data-account-drawer-view-target]')
    if (viewTarget) {
      this.showView(viewTarget, viewTarget.dataset.accountDrawerViewTarget)
      return
    }

    const dateButton = target.closest('[data-account-drawer-date-open]')
    if (dateButton) {
      this.openDatePicker(dateButton)
      return
    }

    const reorder = target.closest('[data-account-drawer-reorder]')
    if (reorder) {
      this.orderAgain(reorder)
      return
    }

    const apply = target.closest('[data-account-drawer-apply]')
    if (apply) {
      this.applyDiscount(apply)
      return
    }

    const copy = target.closest('[data-account-drawer-copy]')
    if (copy) {
      this.copyCode(copy)
      return
    }

    const deleteAddress = target.closest('[data-account-drawer-address-delete]')
    if (deleteAddress) {
      this.deleteAddress(deleteAddress)
    }
  }

  onDateInput(event) {
    const text = event.target.closest('[data-account-drawer-date-text]')
    if (!text) return

    const digits = text.value.replace(/\D/g, '').slice(0, 8)
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean)
    const formatted = parts.join('/')

    if (text.value !== formatted) text.value = formatted

    this.syncDate(text)
  }

  syncDate(text) {
    const wrapper = text.closest('[data-account-drawer-date]')
    const hidden = wrapper.querySelector('[data-account-drawer-date-value]')
    const picker = wrapper.querySelector('[data-account-drawer-date-picker]')
    const match = text.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)

    text.setCustomValidity('')
    hidden.value = ''

    if (!match) {
      if (text.value) text.setCustomValidity(text.dataset.error || 'Invalid date')
      return
    }

    const [, day, month, year] = match
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    const isRealDate =
      date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)

    if (!isRealDate || date > new Date() || Number(year) < 1900) {
      text.setCustomValidity(text.dataset.error || 'Invalid date')
      return
    }

    if (text.dataset.max && `${year}-${month}-${day}` > text.dataset.max) {
      text.setCustomValidity(text.dataset.maxError || text.dataset.error || 'Invalid date')
      return
    }

    hidden.value = `${year}-${month}-${day}`
    if (picker) picker.value = hidden.value
  }

  openDatePicker(button) {
    const wrapper = button.closest('[data-account-drawer-date]')
    const picker = wrapper.querySelector('[data-account-drawer-date-picker]')
    const text = wrapper.querySelector('[data-account-drawer-date-text]')

    try {
      picker.showPicker()
    } catch (error) {
      text.focus()
    }
  }

  onChange(event) {
    const picker = event.target.closest('[data-account-drawer-date-picker]')
    if (picker) {
      const text = picker.closest('[data-account-drawer-date]').querySelector('[data-account-drawer-date-text]')
      const [year, month, day] = picker.value.split('-')
      text.value = picker.value ? `${day}/${month}/${year}` : ''
      this.syncDate(text)
      this.clearError(text)
      return
    }

    const country = event.target.closest('[data-account-drawer-country]')
    if (country) {
      this.updateProvinces(country)
      return
    }

    const form = event.target.closest('form[data-auto-submit]')
    if (form) {
      this.preferencesChanges = (this.preferencesChanges || 0) + 1
      this.submitForm(form)
    }

    const field = event.target.closest('.account-drawer__field, .account-drawer__checkbox')
    if (field && field.classList.contains('has-error')) this.clearError(event.target)
  }

  activateTab(name) {
    const tabs = this.querySelectorAll('[data-account-drawer-tab]')
    const panels = this.querySelectorAll('[data-account-drawer-panel]')

    tabs.forEach((tab) => {
      const isActive = tab.dataset.accountDrawerTab === name
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', String(isActive))
      tab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    panels.forEach((panel) => {
      const isActive = panel.dataset.accountDrawerPanel === name
      panel.classList.toggle('is-active', isActive)
      panel.hidden = !isActive

      if (isActive) {
        const defaultView = panel.querySelector('[data-account-drawer-view]')
        if (defaultView) this.showView(defaultView, defaultView.dataset.accountDrawerView)
      }
    })

    if (this.body) this.body.scrollTop = 0
    if (name === 'profile') this.loadPreferences()
  }

  preferencesKey() {
    return `accountDrawerPreferences:${this.dataset.customerId}`
  }

  getCachedPreferences() {
    if (this.preferenceState) return this.preferenceState

    try {
      const stored = window.sessionStorage.getItem(this.preferencesKey())
      if (stored) this.preferenceState = JSON.parse(stored)
    } catch (error) {}

    return this.preferenceState || null
  }

  setCachedPreferences(state) {
    this.preferenceState = state

    try {
      window.sessionStorage.setItem(this.preferencesKey(), JSON.stringify(state))
    } catch (error) {}
  }

  applyPreferences(form, state) {
    ;[
      ['email_marketing', state.email_marketing],
      ['sms_marketing', state.sms_marketing],
    ].forEach(([name, value]) => {
      const input = form.querySelector(`[name="${name}"]`)
      if (!input || typeof value !== 'boolean') return

      input.checked = value
      input.defaultChecked = value
    })
  }

  readPreferences(form) {
    const email = form.querySelector('[name="email_marketing"]')
    const sms = form.querySelector('[name="sms_marketing"]')

    return {
      email_marketing: Boolean(email && email.checked),
      sms_marketing: Boolean(sms && sms.checked),
    }
  }

  async loadPreferences() {
    const form = this.querySelector('form[data-account-drawer-form="preferences"]')
    if (!form || !form.dataset.endpoint || !this.dataset.customerId) return

    const cached = this.getCachedPreferences()
    const inputs = form.querySelectorAll('input[type="checkbox"]')

    if (cached) {
      this.applyPreferences(form, cached)
    } else {
      inputs.forEach((input) => {
        input.disabled = true
      })
      form.setAttribute('aria-busy', 'true')
    }

    const requestId = (this.preferencesRequest = (this.preferencesRequest || 0) + 1)
    const changesBefore = this.preferencesChanges || 0

    try {
      const status = await this.post(form.dataset.endpoint, { form: 'status', ...this.identity() })
      const isLatest = requestId === this.preferencesRequest
      const userChangedMeanwhile = (this.preferencesChanges || 0) !== changesBefore

      if (isLatest && !userChangedMeanwhile && form.isConnected) {
        const state = {
          email_marketing: typeof status.email_marketing === 'boolean' ? status.email_marketing : undefined,
          sms_marketing: typeof status.sms_marketing === 'boolean' ? status.sms_marketing : undefined,
        }
        this.applyPreferences(form, state)
        this.setCachedPreferences(this.readPreferences(form))
      }
    } catch (error) {
      console.error('[account-drawer] Could not load marketing preferences', error)
    } finally {
      inputs.forEach((input) => {
        input.disabled = false
      })
      form.removeAttribute('aria-busy')
    }
  }

  showView(origin, name) {
    const panel = origin.closest('[data-account-drawer-panel]')
    if (!panel) return

    panel.querySelectorAll('[data-account-drawer-view]').forEach((view) => {
      view.hidden = view.dataset.accountDrawerView !== name
    })

    if (this.body) this.body.scrollTop = 0
  }

  cartRoute(path) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
    return `${root}${path}`
  }

  async getCartJson() {
    const response = await fetch(this.cartRoute('cart.js'), { cache: 'no-store' })
    return response.json()
  }

  isCodeApplied(cart, code) {
    const upper = code.toUpperCase()
    const entry = (cart.discount_codes || []).find((discount) => String(discount.code).toUpperCase() === upper)
    if (entry && entry.applicable) return true

    const cartLevel = (cart.cart_level_discount_applications || []).some(
      (discount) => String(discount.title).toUpperCase() === upper
    )
    const lineLevel = (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some(
        (allocation) =>
          allocation.discount_application && String(allocation.discount_application.title).toUpperCase() === upper
      )
    )

    return cartLevel || lineLevel
  }

  setApplyState(button, state, message) {
    const help = button.parentElement.querySelector('[data-account-drawer-apply-help]')
    const labels = {
      idle: button.dataset.applyLabel,
      applying: button.dataset.applyingLabel,
      applied: button.dataset.appliedLabel,
    }

    button.textContent = labels[state] || labels.idle
    button.classList.toggle('is-applied', state === 'applied')
    if (state === 'applying') {
      button.setAttribute('aria-busy', 'true')
    } else {
      button.removeAttribute('aria-busy')
    }

    if (state === 'applied') {
      button.setAttribute('aria-disabled', 'true')
    } else {
      button.removeAttribute('aria-disabled')
    }

    if (help) {
      help.textContent = message || help.dataset.defaultText
      help.classList.toggle('is-error', Boolean(message))
    }
  }

  refreshCartQuietly() {
    const cartItems = document.querySelector('cart-items')
    if (!cartItems || typeof cartItems.getCart !== 'function') return

    const stopAutoOpen = (event) => event.stopImmediatePropagation()
    window.addEventListener('theme:product:added', stopAutoOpen, { capture: true, once: true })
    setTimeout(() => window.removeEventListener('theme:product:added', stopAutoOpen, { capture: true }), 5000)

    cartItems.getCart()
  }

  async applyDiscount(button) {
    if (button.getAttribute('aria-disabled') === 'true' || button.hasAttribute('aria-busy')) return

    const code = button.dataset.accountDrawerApply
    clearTimeout(button.applyTimer)
    this.setApplyState(button, 'applying')

    try {
      const current = await this.getCartJson()
      const otherCodes = (current.discount_codes || [])
        .map((discount) => discount.code)
        .filter((existing) => String(existing).toUpperCase() !== code.toUpperCase())

      await fetch(this.cartRoute('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...otherCodes, code].join(',') }),
      })

      const updated = await this.getCartJson()

      if (!this.isCodeApplied(updated, code)) {
        const help = button.parentElement.querySelector('[data-account-drawer-apply-help]')
        const message = updated.item_count === 0 && help ? help.dataset.defaultText : button.dataset.errorLabel
        throw new Error(message)
      }

      this.setApplyState(button, 'applied')
      this.refreshCartQuietly()
    } catch (error) {
      console.error('[account-drawer] Could not apply discount', error)
      this.setApplyState(button, 'idle', error.message || button.dataset.errorLabel)
      button.applyTimer = setTimeout(() => this.setApplyState(button, 'idle'), 5000)
    }
  }

  async orderAgain(button) {
    if (button.getAttribute('aria-busy') === 'true') return

    let items = []
    try {
      items = JSON.parse(button.dataset.accountDrawerReorder || '[]')
    } catch (error) {
      items = []
    }
    if (!items.length) return

    const label = button.dataset.label || button.textContent.trim()
    button.dataset.label = label
    button.textContent = button.dataset.addingLabel || label
    button.setAttribute('aria-busy', 'true')

    const addItems = (list) =>
      fetch(this.cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: list }),
      }).then((response) => response.ok)

    let added = 0
    try {
      if (await addItems(items)) {
        added = items.length
      } else {
        for (const item of items) {
          if (await addItems([item])) added += 1
        }
      }
    } catch (error) {
      console.error('[account-drawer] Order again failed', error)
    }

    button.removeAttribute('aria-busy')

    if (added < items.length) {
      button.textContent = button.dataset.errorLabel || label
      setTimeout(() => {
        button.textContent = label
      }, 4000)
    } else {
      button.textContent = label
    }

    if (added > 0) {
      const cartItems = document.querySelector('cart-items')
      if (cartItems && typeof cartItems.getCart === 'function') {
        cartItems.getCart()
      } else {
        window.location.href = this.cartRoute('cart')
      }
    }
  }

  async onCartChange() {
    const buttons = this.querySelectorAll('[data-account-drawer-apply]')
    if (!buttons.length) return

    try {
      const cart = await this.getCartJson()
      buttons.forEach((button) => {
        if (button.hasAttribute('aria-busy')) return
        const applied = this.isCodeApplied(cart, button.dataset.accountDrawerApply)
        const isShownApplied = button.classList.contains('is-applied')
        if (applied !== isShownApplied) this.setApplyState(button, applied ? 'applied' : 'idle')
      })
    } catch (error) {}
  }

  copyCode(button) {
    const code = button.dataset.accountDrawerCopy
    const label = button.dataset.originalLabel || button.textContent.trim()
    button.dataset.originalLabel = label

    const done = () => {
      button.textContent = button.dataset.copiedLabel || label
      clearTimeout(button.copyTimer)
      button.copyTimer = setTimeout(() => {
        button.textContent = label
      }, 2000)
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(done).catch(() => this.copyFallback(code, done))
    } else {
      this.copyFallback(code, done)
    }
  }

  copyFallback(code, done) {
    const input = document.createElement('textarea')
    input.value = code
    input.setAttribute('readonly', '')
    input.style.position = 'fixed'
    input.style.opacity = '0'
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    input.remove()
    done()
  }

  onSubmit(event) {
    const form = event.target

    if (form.classList.contains('account-drawer__product-form')) {
      if (!window.theme || !window.theme.hasCartDrawer) return

      event.preventDefault()
      const button = form.querySelector('[data-account-drawer-add]')
      document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { button } }))
      return
    }

    if (form.hasAttribute('data-account-drawer-form')) {
      event.preventDefault()
      if (this.validate(form)) this.submitForm(form)
    }
  }

  validate(form) {
    let firstInvalid = null

    form.querySelectorAll('[data-account-drawer-date-text]').forEach((text) => this.syncDate(text))
    form.querySelectorAll('input[type="tel"]').forEach((input) => this.checkPhone(input))
    this.checkSmsPhone(form)

    form.querySelectorAll('input, select').forEach((input) => {
      if (input.disabled || input.type === 'hidden' || input.hasAttribute('data-account-drawer-date-picker')) return

      this.clearError(input)
      if (input.checkValidity()) return

      const message = input.validity.customError
        ? input.validationMessage
        : input.dataset.error || input.validationMessage

      if (this.markError(input, message) && !firstInvalid) firstInvalid = input
    })

    if (firstInvalid) firstInvalid.focus()

    return !firstInvalid
  }

  checkPhone(input) {
    input.setCustomValidity('')

    const value = input.value.trim()
    if (!value) return

    const compact = value.replace(/[\s().-]/g, '')
    const digits = compact.replace(/^\+/, '')
    let isValid = /^\+?\d+$/.test(compact)

    if (isValid) {
      if (compact.startsWith('+')) {
        isValid = digits.startsWith('61') ? /^61[2-478]\d{8}$/.test(digits) : digits.length >= 8 && digits.length <= 15
      } else if (digits.startsWith('0')) {
        isValid = /^0[2-478]\d{8}$/.test(digits)
      } else if (digits.startsWith('61')) {
        isValid = /^61[2-478]\d{8}$/.test(digits)
      } else {
        isValid = /^4\d{8}$/.test(digits)
      }
    }

    if (!isValid) input.setCustomValidity(this.dataset.phoneError || 'Please enter a valid phone number')
  }

  checkSmsPhone(form) {
    const sms = form.querySelector('input[name="sms_marketing"]')
    const phone = form.querySelector('input[type="tel"][name="phone"]')
    if (!sms || !phone || !sms.checked || phone.value.trim()) return

    phone.setCustomValidity(this.dataset.smsPhoneError || 'Please add a phone number to receive SMS updates')
  }

  markError(input, text) {
    const wrapper = input.closest('.account-drawer__field, .account-drawer__checkbox')
    if (!wrapper) return false

    this.clearError(input)
    wrapper.classList.add('has-error')
    input.setAttribute('aria-invalid', 'true')

    const message = document.createElement('p')
    message.className = 'account-drawer__error'
    message.id = `${input.id || input.name}-error`
    message.textContent = text
    input.setAttribute('aria-describedby', message.id)

    if (wrapper.classList.contains('account-drawer__checkbox')) {
      wrapper.after(message)
    } else {
      wrapper.append(message)
    }

    return true
  }

  showFieldErrors(form, result) {
    const errors = result && Array.isArray(result.errors) ? result.errors : []
    const aliases = { country_code: 'country', province_code: 'province', birth_date: 'dob', date_of_birth: 'dob' }
    let firstInvalid = null

    errors.forEach((error) => {
      const rawField = typeof error === 'string' ? error : [].concat(error && error.field).filter(Boolean).pop()
      if (!rawField) return

      const snake = String(rawField).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
      const name = aliases[snake] || snake
      let input = form.querySelector(`[name="${name}"]`)

      if (input && input.hasAttribute('data-account-drawer-date-value')) {
        input = input.closest('[data-account-drawer-date]').querySelector('[data-account-drawer-date-text]')
      }

      if (!input || input.type === 'hidden') return

      const text = (typeof error === 'object' && error.message) || input.dataset.error || result.message
      if (this.markError(input, text) && !firstInvalid) firstInvalid = input
    })

    if (firstInvalid) firstInvalid.focus()

    return Boolean(firstInvalid)
  }

  clearError(input) {
    const wrapper = input.closest('.account-drawer__field, .account-drawer__checkbox')
    if (!wrapper) return

    wrapper.classList.remove('has-error')
    input.removeAttribute('aria-invalid')
    input.removeAttribute('aria-describedby')

    const error = wrapper.classList.contains('account-drawer__checkbox')
      ? wrapper.nextElementSibling
      : wrapper.querySelector('.account-drawer__error')

    if (error && error.classList.contains('account-drawer__error')) error.remove()
  }

  serialize(form) {
    const data = {}

    form.querySelectorAll('input, select').forEach((input) => {
      if (!input.name || input.disabled) return
      data[input.name] = input.type === 'checkbox' ? input.checked : input.value.trim()
    })

    return { ...data, ...this.identity() }
  }

  identity() {
    if (!this.dataset.customerId) return {}

    return {
      customer_id: this.dataset.customerId,
      customer_email: this.dataset.customerEmail,
      signature: this.dataset.signature || '',
    }
  }

  async post(endpoint, payload) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })

    const result = await response.json().catch(() => ({}))

    if (!response.ok || (result && result.success === false)) {
      const error = new Error((result && result.message) || `Request failed: ${response.status}`)
      error.userMessage = result && result.message
      error.result = result
      throw error
    }

    return result
  }

  async submitForm(form) {
    const endpoint = form.dataset.endpoint
    const message = form.querySelector('[data-account-drawer-message]')
    const submit = form.querySelector('[type="submit"]')
    const type = form.dataset.accountDrawerForm
    const payload = { ...this.serialize(form), form: type }

    if (message) {
      message.hidden = true
      message.classList.remove('is-error')
    }

    if (!endpoint) {
      console.warn(`[account-drawer] No endpoint configured for the "${type}" form`, payload)
      this.showMessage(message, this.dataset.errorMessage, true)
      return
    }

    if (submit) submit.setAttribute('aria-busy', 'true')

    try {
      const result = await this.post(endpoint, payload)

      if (type === 'register' && form.dataset.loginUrl) {
        this.rememberLogin()
        const loginUrl = new URL(form.dataset.loginUrl, window.location.origin)
        loginUrl.searchParams.set('return_to', window.location.pathname + window.location.search)
        loginUrl.searchParams.set('login_hint', payload.email || '')
        window.location.href = loginUrl.toString()
      } else if (type === 'register') {
        form.reset()
        this.activateTab('login')
        const loginPanel = this.querySelector('[data-account-drawer-panel="login"]')
        this.showMessage(loginPanel && loginPanel.querySelector('[data-account-drawer-notice]'), this.dataset.createdMessage, false)
      } else if (type === 'complete') {
        this.preferenceState = null
        try {
          window.sessionStorage.removeItem(this.preferencesKey())
        } catch (error) {}
        await this.refresh('foryou', null, (html) => !html.querySelector('[data-account-drawer-complete]'))
      } else if (type === 'preferences') {
        form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.defaultChecked = input.checked
        })
        this.setCachedPreferences(this.readPreferences(form))
        this.showMessage(message, this.dataset.savedMessage, false)
      } else {
        let isUpToDate = null
        if (type === 'address' && payload.action === 'update') {
          isUpToDate = this.addressValuesCheck(payload)
        } else if (type === 'address') {
          isUpToDate = this.addressCheck(result && result.address_id, true)
        }
        await this.refresh('profile', this.dataset.savedMessage, isUpToDate)
      }
    } catch (error) {
      console.error('[account-drawer]', error)
      if (!this.showFieldErrors(form, error.result)) {
        this.showMessage(message, error.userMessage || this.dataset.errorMessage, true)
      }

      if (type === 'preferences') {
        form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.checked = input.defaultChecked
        })
      }
    } finally {
      if (submit) submit.removeAttribute('aria-busy')
    }
  }

  async fetchDrawerMarkup(sectionId) {
    const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}&_=${Date.now()}`
    const response = await fetch(url, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } })
    const sections = await response.json()
    return new DOMParser().parseFromString(sections[sectionId], 'text/html')
  }

  async refresh(tab, notice, isUpToDate) {
    const wrapper = this.closest('[data-section-id]')
    const sectionId = wrapper && wrapper.dataset.sectionId
    const retryDelays = isUpToDate ? [0, 700, 1200, 2000, 3000] : [0]

    try {
      let html = null

      for (const delay of retryDelays) {
        if (delay) await new Promise((resolve) => setTimeout(resolve, delay))
        html = await this.fetchDrawerMarkup(sectionId)
        if (!isUpToDate || isUpToDate(html)) break
      }

      const freshInner = html.querySelector('[data-account-drawer-inner]')
      const freshDrawer = html.querySelector('account-drawer')

      if (!freshInner) throw new Error('Account drawer markup not found')

      this.inner.innerHTML = freshInner.innerHTML
      if (freshDrawer && freshDrawer.dataset.signature) this.dataset.signature = freshDrawer.dataset.signature
      if (freshDrawer && freshDrawer.dataset.customerEmail) this.dataset.customerEmail = freshDrawer.dataset.customerEmail
      this.body = this.querySelector('.account-drawer__body')
      this.initCountries()
      this.activateTab(tab)

      const panel = this.querySelector(`[data-account-drawer-panel="${tab}"]`)
      const noticeElement = panel && panel.querySelector('[data-account-drawer-notice]')
      this.showMessage(noticeElement, notice, false)
    } catch (error) {
      console.error('[account-drawer]', error)
      window.location.reload()
    }
  }

  addressValuesCheck(payload) {
    const numericId = String(payload.address_id || '').split('/').pop().split('?')[0]
    if (!numericId) return null

    return (html) =>
      ['address1', 'city', 'zip'].every((field) => {
        const input = html.getElementById(`address-${numericId}-${field}`)
        return input && (input.getAttribute('value') || '').trim() === String(payload[field] || '').trim()
      })
  }

  addressCheck(addressId, shouldExist) {
    const numericId = String(addressId || '').split('/').pop().split('?')[0]
    if (!numericId) return null

    return (html) => Boolean(html.querySelector(`[data-account-drawer-view="address-${numericId}"]`)) === shouldExist
  }


  showMessage(element, text, isError) {
    if (!element) return

    element.textContent = text || ''
    element.classList.toggle('is-error', isError)
    element.hidden = !text
  }

  async deleteAddress(button) {
    if (button.dataset.confirm && !window.confirm(button.dataset.confirm)) return

    const endpoint = button.dataset.endpoint
    const payload = {
      form: 'address',
      action: 'delete',
      address_id: button.dataset.accountDrawerAddressDelete,
      ...this.identity(),
    }

    if (!endpoint) {
      console.warn('[account-drawer] No endpoint configured for address delete', payload)
      window.alert(this.dataset.errorMessage)
      return
    }

    button.setAttribute('aria-busy', 'true')

    try {
      await this.post(endpoint, payload)
      await this.refresh('profile', this.dataset.savedMessage, this.addressCheck(payload.address_id, false))
    } catch (error) {
      console.error('[account-drawer]', error)
      window.alert(error.userMessage || this.dataset.errorMessage)
      button.removeAttribute('aria-busy')
    }
  }

  initCountry(select) {
    const value = select.dataset.default
    if (value) {
      const option = Array.from(select.options).find((item) => item.value === value || item.textContent === value)
      if (option) select.value = option.value
    }

    this.updateProvinces(select, true)
  }

  updateProvinces(countrySelect, useDefault = false) {
    const form = countrySelect.closest('form')
    const provinceSelect = form && form.querySelector('[data-account-drawer-province]')
    if (!provinceSelect) return

    const placeholder = provinceSelect.options[0] ? provinceSelect.options[0].cloneNode(true) : null
    const option = countrySelect.options[countrySelect.selectedIndex]
    let provinces = []

    try {
      provinces = JSON.parse((option && option.dataset.provinces) || '[]')
    } catch (error) {
      provinces = []
    }

    provinceSelect.innerHTML = ''
    if (placeholder) provinceSelect.append(placeholder)

    provinces.forEach(([value, label]) => {
      provinceSelect.append(new Option(label, value))
    })

    provinceSelect.disabled = provinces.length === 0

    if (useDefault && provinceSelect.dataset.default) {
      const match = Array.from(provinceSelect.options).find(
        (item) => item.value === provinceSelect.dataset.default || item.textContent === provinceSelect.dataset.default
      )
      if (match) provinceSelect.value = match.value
    }
  }
}

if (!customElements.get('account-drawer')) {
  customElements.define('account-drawer', AccountDrawer)
}
